import { supabase } from './supabaseClient';

export interface InvitationData {
  id: string;
  team_id: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  invited_by: string;
  invited_at: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'expired';
  token: string;
}

/**
 * Create a team invitation for a user not yet in the system
 */
export const createTeamInvitation = async (
  teamId: string,
  email: string,
  role: 'admin' | 'member' | 'viewer',
  invitedBy: string,
  invitation_type: string = 'project'
): Promise<{ data: InvitationData | null; error: Error | null }> => {
  try {
    // Check if the user is already a team member
    await checkIfUserIsTeamMember(teamId, email);

    // Generate a secure token
    const token = crypto.randomUUID();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiry

    const { data, error } = await supabase
      .from('team_invitations')
      .insert({
        team_id: teamId,
        email: email.toLowerCase(),
        role,
        invited_by: invitedBy,
        expires_at: expiresAt.toISOString(),
        token,
        status: 'pending'
      })
      .select()
      .single();

    if (error) {
      // UNIQUE constraint violation (duplicate pending invite)
      if (error.code === '23505') {
        throw new Error('User has already been invited to this team');
      }

      throw error;
    }

    console.log(`🔗 Invitation link: ${window.location.origin}/invite/${token}`);

    const { error: invitationError } = await supabase.functions.invoke('send-invite-email', {
      body: {
        email,
        token,
        role,
        invitation_type
      }
    });

    if (invitationError) {
      console.error('Error invoking send-invite-email function:', invitationError);
      throw new Error('Failed to send invitation email');
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error in createTeamInvitation:', error);
    return { data: null, error };
  }
};

/**
 * Check if user is already a member of the team before sending invitation 
 */
export const checkIfUserIsTeamMember = async (
  teamId: string,
  email: string
): Promise<any> => {
  const { data, error } = await supabase
    .from('team_members')
    .select(`
        id,
        users!team_members_user_id_fkey!inner(email)
      `)
    .eq('team_id', teamId)
    .eq('users.email', email.toLowerCase())
    .maybeSingle();

  if (error) throw error;

  if (data) {
    throw new Error('User is already a member of the team');
  }

  return data;
};

/**
 * Get pending invitations for a team
 */
export const getTeamInvitations = async (
  teamId: string
): Promise<{ data: InvitationData[] | null; error: Error | null }> => {
  try {
    const { data, error } = await supabase
      .from('team_invitations')
      .select('*')
      .eq('team_id', teamId)
      .eq('status', 'pending')
      .order('invited_at', { ascending: false });

    if (error) {
      console.error('Error fetching team invitations:', error);
      return { data: null, error };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error in getTeamInvitations:', error);
    return { data: null, error: error as Error };
  }
};

/**
 * Cancel a team invitation
 */
export const cancelTeamInvitation = async (
  invitationId: string
): Promise<{ success: boolean; error: Error | null }> => {
  try {
    const { error } = await supabase
      .from('team_invitations')
      .update({ status: 'cancelled' })
      .eq('id', invitationId);

    if (error) {
      console.error('Error canceling team invitation:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in cancelTeamInvitation:', error);
    return { success: false, error: error as Error };
  }
}; 